import React from 'react';
import { Bell, BellRing, Check, X, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { BannerState, useNotificationBanner } from '../../CustomHooks/Agents/useBrowserNotification';
import { notificationBannerVariants } from './notificationBannerVariants';

const textKeys: Record<BannerState, string> = {
  prompt: 'Agent.NotificationBannerText',
  waiting: 'Agent.NotificationWaiting',
  granted: 'Agent.NotificationEnabled',
  denied: 'Agent.NotificationDenied',
};

const BannerIcon: React.FC<{ state: BannerState; className: string }> = ({ state, className }) => {
  switch (state) {
    case 'waiting':
      return <BellRing size={16} className={`${className} animate-pulse`} />;
    case 'granted':
      return <Check size={16} className={className} />;
    case 'denied':
      return <XCircle size={16} className={className} />;
    default:
      return <Bell size={16} className={className} />;
  }
};

const NotificationBanner: React.FC = () => {
  const { t } = useTranslation();
  const { shouldShowBanner, bannerState, requestPermission, dismiss } = useNotificationBanner();

  if (!shouldShowBanner) return null;

  const { container, text, icon, dismissButton } = notificationBannerVariants({ variant: bannerState });

  return (
    <div className={container()}>
      <BannerIcon state={bannerState} className={icon()} />
      <span className={text()}>{t(textKeys[bannerState])}</span>

      {bannerState === 'prompt' && (
        <button
          type="button"
          onClick={requestPermission}
          className="text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-md px-3 py-1 transition-colors cursor-pointer border-none outline-none focus:outline-none whitespace-nowrap">
          {t('Agent.NotificationEnable')}
        </button>
      )}

      <button
        type="button"
        onClick={dismiss}
        className={dismissButton()}
        title={t('Agent.NotificationDismiss')}
        aria-label={t('Agent.NotificationDismiss')}>
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  );
};

export default NotificationBanner;
